// import 'core-js/stable';
// import 'regenerator-runtime/runtime';

import prepareHierarchy from './hierarchy';

const events = (options = {}) => {
	const hierarchy = prepareHierarchy(options.hierarchy);

	const store = {}

	const prepareStoreForEvent = event => {
		store[event] = store[event] || {
			on: [],
			once: [],
			history: [],
		}
		return store[event];
	}

	const on = (events, fn, fired) => {
		return subscribe(_on, events, fn, fired);
	}
	const once = (events, fn, fired) => {
		return subscribe(_once, events, fn, fired);
	}
	const subscribe = (subscribeFn, events, fn, fired) => {
		events = Array.isArray(events) ? events : [events];
		const _methods = events.map(event => subscribeFn(event, fn, fired));
		const methods = {
			off: () => _methods.forEach(({ off }) => off()),
			ignore: () => {
				const ignores = [];
				_methods.forEach(({ ignore }) => {
					const _ignore = ignore();
					ignores.push(_ignore);
				});
				events.forEach(event => {
					_once(event, () => {
						ignores.forEach(({ cancel }) => cancel());
					});
				});
			},
		}
		return methods;
	}
	const _on = (event, fn, fired) => {
		const eventStore = prepareStoreForEvent(event);
		const history = eventStore.history;
		if (fired && history.length > 0) {
			const data = history[history.length - 1];
			fn(...data);
		}
		const methods = {
			off: () => off(event, fn),
			ignore: () => ignoreOn(event, fn),
		}
		if (eventStore.on.includes(fn)) {
			return methods;
		}
		eventStore.on.push(fn);
		return methods;
	}

	const _once = (event, fn, fired) => {
		const eventStore = prepareStoreForEvent(event);
		const history = eventStore.history;
		if (fired && history.length > 0) {
			const data = history[history.length - 1];
			fn(...data);
		} else {
			if (eventStore.once.includes(fn)) {
				return;
			}
			eventStore.once.push(fn);
		}
		return {
			off: () => off(event, fn),
			ignore: () => ignoreOnce(event, fn),
		}
	}
	once.promise = (events, fired) => {
		return new Promise((resolve, reject) => {
			once(events, resolve, fired);
		});
	}

	const onceAll = (events, fn, fired) => {
		events = Array.isArray(events) ? events : [events];
		let i = 0;
		const results = {}
		const eventFired = (event, result) => {
			i++;
			results[event] = result;
			if (i === events.length) {
				fn(results);
			}
		}
		const off = [];
		events.forEach(event => {
			const { off: _off } = once(event, (...result) => eventFired(event, result), fired);
			off.push(_off);
		});
		return {
			off: () => off.forEach(_off => _off()),
		}
	}
	onceAll.promise = (events, fired) => {
		return new Promise((resolve, reject) => {
			onceAll(events, resolve, fired);
		});
	}

	const onceAny = (events, fn, fired) => {
		events = Array.isArray(events) ? events : [events];
		let i = 0;
		const eventFired = () => {
			i++;
			if (i === 1) {
				fn();
			}
		}
		const off = [];
		events.forEach(event => {
			const { off: _off } = once(event, eventFired, fired);
			off.push(_off);
		});
		return {
			off: () => off.forEach(_off => _off()),
		}
	}
	onceAny.promise = (events, fired) => {
		return new Promise((resolve, reject) => {
			onceAny(events, resolve, fired);
		});
	}

	const offAll = () => {
		Object.keys(store).forEach(event => {
			delete store[event];
		});
	}
	const offEvent = event => {
		delete store[event];
	}
	const off = (event, fn) => {

		if (!event) {
			offAll();
			return;
		}
		if (!fn) {
			offEvent(event);
			return;
		}

		const eventStore = prepareStoreForEvent(event);
		let removed = 0;

		const onIndex = eventStore.on.findIndex(_fn => _fn === fn);
		if (onIndex > -1) {
			eventStore.on.splice(onIndex, 1);
			removed++;
		}

		const onceIndex = eventStore.once.findIndex(_fn => _fn === fn);
		if (onceIndex > -1) {
			eventStore.once.splice(onceIndex, 1);
			removed++;
		}

		return removed > 0;
	}

	const fire = (event, ...data) => {

		const eventsFired = {}
		const fnsFired = [];

		const fireFn = (fn, options = {}) => {
			const _fnsFired = (options.fnsFired || fnsFired);
			if (_fnsFired.includes(fn)) {
				return;
			}
			try {
				fn(...data);
			} catch (e) {
				console.error(e);
			}
			_fnsFired.push(fn);
		}
		const fireEvent = event => {

			if (eventsFired[event]) {
				return;
			}
			eventsFired[event] = true;

			const _fireEvent = ({ fnsFired } = {}) => {
				const eventStore = prepareStoreForEvent(event);
				eventStore.on.forEach(fn => fireFn(fn, { fnsFired }));
				eventStore.once.forEach(fn => fireFn(fn, { fnsFired }));
				eventStore.once = [];
				eventStore.history.push([...data]);
			}

			if (batchEvents) {
				batchAddEvent(event, _fireEvent);
			} else {
				_fireEvent();
			}
		}
		const fireConnectedEvents = event => {

			const connectedStraight = hierarchy.straight[event] || [];
			connectedStraight.forEach(fireEvent);

			const connectedReversed = hierarchy.reversed[event] || [];
			connectedReversed.forEach(fireEvent);
		}

		fireEvent(event);
		fireConnectedEvents(event);
	}

	let batchEvents = null;
	const batchAddEvent = (event, fn) => {
		batchEvents.order = batchEvents.order.filter(_event => _event !== event);
		batchEvents.order.push(event);
		batchEvents.fns[event] = fn;
	}
	const batchRunEvents = () => {
		const _batchEvents = batchEvents;
		batchEvents = null;
		const fnsFired = [];
		_batchEvents.order.forEach(event => {
			const fn = _batchEvents.fns[event];
			fn({
				fnsFired,
			});
		});
	}
	const batch = fn => {

		if (batchEvents) {
			batchEvents.level++
		} else {
			batchEvents = {
				order: [], // order of events
				fns: {}, // fire fn by event
				level: 1, // if a batch inside a batch
			};
		}

		fn();

		if (batchEvents.level === 1) {
			batchRunEvents();
		} else {
			batchEvents.level--;
		}
	}

	const ignoreOn = (event, fn) => {
		return _ignore(on, event, fn);
	}
	const ignoreOnce = (event, fn) => {
		return _ignore(once, event, fn);
	}
	const _ignore = (subscribeFn, event, fn) => {
		const removed = off(event, fn);
		const methods = {
			cancel: () => { },
		}
		if (removed) {
			const reSubscribe = () => subscribeFn(event, fn);
			const { off } = once(event, reSubscribe);
			methods.cancel = () => {
				off();
				reSubscribe();
			}
		}
		return methods;
	}

	return {
		on,
		once,
		onceAll,
		onceAny,
		off,
		fire,
		batch,
		ignore: {
			on: ignoreOn,
			once: ignoreOnce,
		},
	}
}

export default events;